'use strict';

var tslib = require('tslib');
var neverthrow = require('neverthrow');
var singleton = require('@keenondrums/singleton');
var AuthError = require('../../errors/auth/AuthError.js');
var AuthWindowBlockedError = require('../../errors/auth/AuthWindowBlockedError.js');
var AuthWindowClosedError = require('../../errors/auth/AuthWindowClosedError.js');
var logger = require('../../logger.js');
var authApiClient = require('../authApiClient.js');
var helpers = require('../helpers.js');
var subscriptions = require('../subscriptions.js');

exports.UserManager = class UserManager {
    _params;
    _authApiClient;
    _logger;
    _childWindow;
    constructor(params) {
        this._params = params || {};
        this._authApiClient = new authApiClient.AuthApiClient({ prefix: this._params.prefix, webPath: this._params.webPath });
        this._logger = new logger.default('UserManager');
        this._childWindow = null;
    }
    _getWebPath() {
        if (typeof window === 'undefined')
            return undefined;
        if (this._params.webPath)
            return this._params.webPath;
        return window.location.origin;
    }
    _getWindowFeatures(width, height) {
        const getSilentAuthModalPosition = (width, height) => {
            if (typeof window === 'undefined')
                return null;
            // Fixes dual-screen position                             Most browsers      Firefox
            const screenDualLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
            const screenDualTop = window.screenTop !== undefined ? window.screenTop : window.screenY;
            const screenWidth = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
            const screenHeight = window.innerHeight || document.documentElement.clientHeight || window.screen.height;
            const top = (screenHeight - height) / 2 + screenDualTop;
            const left = (screenWidth - width) / 2 + screenDualLeft;
            return {
                top,
                left,
            };
        };
        const modalPosition = getSilentAuthModalPosition(width, height);
        return `width=${width}, height=${height}, top=${modalPosition ? modalPosition.top : 0}, left=${modalPosition ? modalPosition.left : 0}`;
    }
    _getWindowParams() {
        const { type = 'modal', width = 430, height = 600 } = this._params;
        const isNewWindow = ['modal', 'tab'].includes(type);
        const target = isNewWindow ? '_blank' : undefined;
        const features = ['modal'].includes(type) ? this._getWindowFeatures(width, height) : undefined;
        return {
            isNewWindow,
            target,
            features,
        };
    }
    _startChildWindowCloseWatcher(callback) {
        if (typeof window === 'undefined')
            return undefined;
        if (!this._childWindow)
            return undefined;
        return setInterval(() => {
            if (!this._childWindow ||
                this._childWindow.window === null ||
                this._childWindow.window === undefined ||
                this._childWindow.closed) {
                callback();
            }
        }, 500);
    }
    _startChildWindowTimeoutWatcher(timeout, callback) {
        if (typeof window === 'undefined')
            return undefined;
        if (!this._childWindow)
            return undefined;
        return setTimeout(() => {
            callback();
        }, timeout);
    }
    _destroyAuthenticationChildWindow() {
        if (typeof window === 'undefined')
            return undefined;
        // При вызове этого кода синхронно сафари на ios теряет указатель на this._childWindow
        // А при отложенном вызове он есть
        setTimeout(() => {
            if (!this._childWindow) {
                return undefined;
            }
            this._childWindow.close();
            this._childWindow = null;
        }, 0);
    }
    async _loginV2ByChildWindow(params) {
        if (typeof window === 'undefined')
            return neverthrow.err(new Error('No window defined'));
        const { prefix, analytics: { productId } = {} } = this._params;
        const webPath = this._getWebPath();
        const { isNewWindow } = this._getWindowParams();
        let url = '';
        try {
            const { otac, username } = await this._authApiClient.verifyPhoneNumberByReferenceToken(params);
            url = helpers.getLoginUrl({
                otac,
                hint: username,
                prefix,
                webPath,
                loginType: 'otac',
                isNewWindow,
                // Это нужно для логина под новым юзером, даже если мы уже залогинены под каким-то другим
                prompt: 'login',
                productId,
            });
        }
        catch (error) {
            if (error instanceof Error) {
                this._logger.error(error.message);
                this._destroyAuthenticationChildWindow();
                return neverthrow.err(error);
            }
        }
        return new Promise((resolve) => {
            const childWindowCloseWatcherId = this._startChildWindowCloseWatcher(() => {
                clearInterval(childWindowCloseWatcherId);
                resolve(neverthrow.err(new AuthWindowClosedError.AuthWindowClosedError()));
            });
            subscriptions.subscribeOnLoginSuccess(() => {
                clearInterval(childWindowCloseWatcherId);
                this._destroyAuthenticationChildWindow();
                resolve(neverthrow.ok(undefined));
            });
            subscriptions.subscribeOnLoginFailure(() => {
                clearInterval(childWindowCloseWatcherId);
                this._destroyAuthenticationChildWindow();
                resolve(neverthrow.err(new AuthError.AuthError()));
            });
            if (!this._childWindow) {
                resolve(neverthrow.err(new AuthWindowBlockedError.AuthWindowBlockedError()));
                return;
            }
            this._childWindow.location.href = url;
            // Для браузеров с повышенными настройками безопастности
            this._childWindow.postMessage({
                authMessageType: 'AUTH_PAGE_CHANGE_URL',
                payload: url,
            }, this._getWebPath() || '*');
            // Хак. не уверен что он работает
            this._childWindow.blur();
            setTimeout(this._childWindow.focus, 0);
        });
    }
    async _loginV2ByRedirects(params) {
        if (typeof window === 'undefined')
            return neverthrow.err(new Error('No window defined'));
        const { prefix, analytics: { productId } = {} } = this._params;
        const webPath = this._getWebPath();
        try {
            const { otac, username } = await this._authApiClient.verifyPhoneNumberByReferenceToken(params);
            const url = helpers.getLoginUrl({
                otac,
                hint: username,
                prefix,
                webPath,
                loginType: 'otac',
                // Это нужно для логина под новым юзером, даже если мы уже залогинены под каким-то другим
                prompt: 'login',
                productId,
                isNewWindow: false,
                isCrossDomains: false,
            });
            window.location.href = url;
        }
        catch (error) {
            if (error instanceof Error) {
                this._logger.error(error.message);
                return neverthrow.err(error);
            }
        }
        return neverthrow.ok(undefined);
    }
    /*
     * Must be called AFTER user action and BEFORE long js task (like ajax request)
     */
    prepare() {
        if (typeof window === 'undefined')
            return neverthrow.ok(undefined);
        const timeout = 10000;
        const { window: { title = 'Вход на сайт | Сравни.ру' } = {}, prefix } = this._params;
        const { target, features } = this._getWindowParams();
        const webPath = this._getWebPath();
        const loadingUrl = helpers.getLoadingUrl({ prefix, webPath, timeoutExpired: Date.now() + timeout });
        const childWindow = window.open(loadingUrl, target, features);
        if (!childWindow) {
            return neverthrow.err(new AuthWindowBlockedError.AuthWindowBlockedError());
        }
        this._childWindow = childWindow;
        this._childWindow.document.title = title;
        this._startChildWindowTimeoutWatcher(timeout, () => this._destroyAuthenticationChildWindow());
        // Хак. не уверен что он работает
        window.blur();
        setTimeout(window.focus, 0);
        return neverthrow.ok(undefined);
    }
    async sendOtpToPhoneNumber(params) {
        const { analytics } = this._params;
        try {
            await this._authApiClient.sendOTPByPhoneNumber({ ...analytics, ...params });
        }
        catch (error) {
            if (error instanceof Error) {
                this._logger.error(error.message);
                return neverthrow.err(error);
            }
        }
        return neverthrow.ok(undefined);
    }
    async getConsents(params) {
        const { analytics: { productId } = {} } = this._params;
        try {
            const data = await this._authApiClient.getConsents({ productId, ...params });
            return neverthrow.ok(data);
        }
        catch (error) {
            if (error instanceof Error) {
                this._logger.error(error.message);
                return neverthrow.err(error);
            }
            return neverthrow.err(new Error());
        }
    }
    async loginV2(params) {
        if (this._childWindow) {
            return this._loginV2ByChildWindow(params);
        }
        return this._loginV2ByRedirects(params);
    }
    /** @deprecated */
    async _loginByChildWindow(params) {
        if (typeof window === 'undefined')
            return neverthrow.err(new Error('No window defined'));
        const { prefix, analytics: { productId } = {} } = this._params;
        const webPath = this._getWebPath();
        const { isNewWindow } = this._getWindowParams();
        let url = '';
        try {
            const { otac, username } = await this._authApiClient.verifyPhoneNumberByOTP({ productId, ...params });
            url = helpers.getLoginUrl({
                otac,
                hint: username,
                prefix,
                webPath,
                loginType: 'otac',
                isNewWindow,
                // Это нужно для логина под новым юзером, даже если мы уже залогинены под каким-то другим
                prompt: 'login',
                productId,
            });
        }
        catch (error) {
            if (error instanceof Error) {
                this._logger.error(error.message);
                this._destroyAuthenticationChildWindow();
                return neverthrow.err(error);
            }
        }
        return new Promise((resolve) => {
            const childWindowCloseWatcherId = this._startChildWindowCloseWatcher(() => {
                clearInterval(childWindowCloseWatcherId);
                resolve(neverthrow.err(new AuthWindowClosedError.AuthWindowClosedError({ phone: params.phone })));
            });
            subscriptions.subscribeOnLoginSuccess(() => {
                clearInterval(childWindowCloseWatcherId);
                this._destroyAuthenticationChildWindow();
                resolve(neverthrow.ok(undefined));
            });
            subscriptions.subscribeOnLoginFailure(() => {
                clearInterval(childWindowCloseWatcherId);
                this._destroyAuthenticationChildWindow();
                resolve(neverthrow.err(new AuthError.AuthError({ phone: params.phone })));
            });
            if (!this._childWindow) {
                resolve(neverthrow.err(new AuthWindowBlockedError.AuthWindowBlockedError({ phone: params.phone })));
                return;
            }
            this._childWindow.location.href = url;
            // Для браузеров с повышенными настройками безопастности
            this._childWindow.postMessage({
                authMessageType: 'AUTH_PAGE_CHANGE_URL',
                payload: url,
            }, this._getWebPath() || '*');
            // Хак. не уверен что он работает
            this._childWindow.blur();
            setTimeout(this._childWindow.focus, 0);
        });
    }
    /** @deprecated */
    async _loginByRedirects(params) {
        if (typeof window === 'undefined')
            return neverthrow.err(new Error('No window defined'));
        const { prefix, analytics: { productId } = {} } = this._params;
        const webPath = this._getWebPath();
        try {
            const { otac, username } = await this._authApiClient.verifyPhoneNumberByOTP({ productId, ...params });
            const url = helpers.getLoginUrl({
                otac,
                hint: username,
                prefix,
                webPath,
                loginType: 'otac',
                // Это нужно для логина под новым юзером, даже если мы уже залогинены под каким-то другим
                prompt: 'login',
                productId,
                isNewWindow: false,
                isCrossDomains: false,
            });
            window.location.href = url;
        }
        catch (error) {
            if (error instanceof Error) {
                this._logger.error(error.message);
                return neverthrow.err(error);
            }
        }
        return neverthrow.ok(undefined);
    }
    /** @deprecated Use loginV2 */
    async login(params) {
        if (this._childWindow) {
            return this._loginByChildWindow(params);
        }
        return this._loginByRedirects(params);
    }
};
exports.UserManager = tslib.__decorate([
    singleton.singleton
], exports.UserManager);
//# sourceMappingURL=userManager.js.map
